import { computed, inject, Injectable, Signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthService } from '@aiwyn/auth';
import { CxpTasksSelectors } from '../state/cxp-tasks-state.selectors';
import { CxpPermissions } from '../models';

/**
 * The single place CXP permissions are resolved: state answers what has loaded,
 * the authenticated user what the caller may do. Components read the composed
 * object and never derive either half.
 */
@Injectable({ providedIn: 'root' })
export class CxpTasksPermissionsService {
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);

  private readonly user = this.authService.getUserSignal(true);
  private readonly statePermissions = this.store.selectSignal(CxpTasksSelectors.getPermissions);

  /** A secure-link session is a guest, whichever flow created the link. */
  private readonly isGuest = computed(() => this.user()?.isSecureLinkUser() === true);

  readonly permissions: Signal<CxpPermissions> = computed(() => ({
    ...this.statePermissions(),
    canManageAccess: !this.isGuest(),
    canViewComments: !this.isGuest(),
    // Two separate calls, not folded together: adding files to the request and
    // seeing what is already in it are different claims, so letting guests do one
    // must never hand them the other.
    canBulkUpload: !this.isGuest(),
    canViewUncategorizedFiles: !this.isGuest(),
  }));

  getPermissionsSnapshot(): CxpPermissions {
    return this.permissions();
  }
}
